import { useState, useEffect, ChangeEvent } from "react";
import styles from "./creditCard.module.css";
import classNames from "classnames";
import { ICreditCard, IOption, IShiftIdDTO } from "./interface";
import AdminService from "../../../services/adminService";
import Select, { SingleValue } from "react-select";
import { Button } from "react-bootstrap";
import { encodeQueryData } from "../../../util/queryUtil";
import Spinner from "../../spinner";
import SortDescIcon from "../../../resource/icons/down-chevron.png";
import SortAscIcon from "../../../resource/icons/up-chevron.png";
import { setGlobalAlert } from '../../../state/action-creators/alert';
import { State } from "../../../state";
import { useDispatch, useSelector } from 'react-redux';

export default function CreditCard() {
  const limitOptions: any[] = [
    { value: 20, label: 20 },
    { value: 50, label: 50 },
    { value: 60, label: 60 },
    { value: 70, label: 70 },
    { value: 80, label: 80 },
    { value: 90, label: 90 },
    { value: 100, label: 100 },
    { value: 150, label: 150 },
    { value: 200, label: 200 },
    { value: 250, label: 250 },
  ];
  const [companyBanks, setCompanyBanks] = useState<IOption[]>();
  const [currentCompanyBank, setCurrentCompanyBank] =
    useState<SingleValue<IOption>>();
  const [isBankFilterActive, setIsBankFilterActive] = useState(false);
  const [isShiftEntityIdSearchActive, setIsShiftEntityIdSearchActive] =
    useState(false);
  const [firstShiftSelectOptions, setFirstShiftSelectOptions] =
    useState<IOption[]>();
  const [lastShiftSelectOptions, setLastShiftSelectOptions] =
    useState<IOption[]>();
  const [firstShift, setFirstShift] = useState<SingleValue<IOption>>();
  const [lastShift, setLastShift] = useState<SingleValue<IOption>>();
  const [sortBy, setSortBy] = useState("asc");
  const [limit, setLimit] = useState<SingleValue<IOption>>(limitOptions[0]);
  const [isBatchIdFilterActive, setIsBatchIdFilterActive] = useState(false);
  const [batchId, setBatchId] = useState<string>();
  const [isAmountFilterActive, setIsAmountFilterActive] = useState(false);
  const [minAmount, setMinAmount] = useState<number>();
  const [maxAmount, setMaxAmount] = useState<number>();
  const [isGeneralFilterActive, setIsGeneralFilterActive] = useState(true);
  const [isShiftLocked, setIsShiftLocked] = useState(true);
  const [isShiftCompleted, setIsShiftCompleted] = useState(true);
  const [isVerified, setIsVerified] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchFailed, setIsFetchFailed] = useState(false);
  const [creditCards, setCreditCards] = useState<ICreditCard[]>();
  const [sortByCreditCardId, setSortByCreditCardId] = useState("asc");
  const [sortContainer, setSortContainer] = useState({
    creditCardId: "asc",
    bankName: "asc",
    batchId: "asc",
    amount: "asc",
    verified: "asc",
    shiftId: "asc",
    shiftLocked: "asc",
    shiftCompleted: "asc",
  });
  const dispatch = useDispatch();
  const languageData = useSelector((state: State) => state.languageReducer);

  useEffect(() => {
    getCompanyBanks();
    getShiftId();
  }, []);

  useEffect(() => {
    // Sort here.
  }, [sortContainer]);

  const getCompanyBanks = async () => {
    try {
      const response = await AdminService.getCompanyBanks();

      const fetchedCompanyBanks: IOption[] = [];

      for (const companyBank of response.data) {
        if (companyBank.active === true) {
          fetchedCompanyBanks.push({
            label: companyBank.bankName,
            value: companyBank.companyBankId,
          });
        }
      }

      setCompanyBanks(fetchedCompanyBanks);
    } catch (error: any) {
      console.dir(error);
      /*
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setNotificationMessage(error.response.data.message);
      } else {
        setNotificationMessage("Can not reach server.");
      }
      setShowNotificationModal(true);
      */
      dispatch(setGlobalAlert(
        error.response?.data?.message || "Cannot reach server.",
        'error',
        error.response?.data?.details
      ));
      setIsLoading(false);
      setIsFetchFailed(true);
    }
  };

  const getShiftId = async () => {
    try {
      const response = await AdminService.getShiftId();
      const selectOptions = response.data.map((dto: IShiftIdDTO) => {
        return { value: dto.shiftsEntityId, label: dto.shiftId };
      });
      setFirstShiftSelectOptions(selectOptions);
      setLastShiftSelectOptions(selectOptions);
    } catch (err: any) {
      console.dir(err);
      /*
      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.message
      ) {
        setNotificationMessage(err.response.data.message);
      } else {
        setNotificationMessage("Can not reach server.");
      }
      setShowNotificationModal(true);
      */
      dispatch(setGlobalAlert(
        err.response?.data?.message || "Cannot reach server.",
        'error',
        err.response?.data?.details
      ));
      setIsLoading(false);
      setIsFetchFailed(true);
    }
  };

  const handleSortByCreditCardId = () => {
    if (sortByCreditCardId === "desc" && creditCards !== undefined) {
      const updatedCreditCard = [...creditCards].sort((a, b) => {
        return a.shiftCreditCardEntityId - b.shiftCreditCardEntityId;
      });
      setCreditCards(updatedCreditCard);
      setSortByCreditCardId("asc");
    }

    if (sortByCreditCardId === "asc" && creditCards !== undefined) {
      const updatedCreditCard = [...creditCards].sort((a, b) => {
        return b.shiftCreditCardEntityId - a.shiftCreditCardEntityId;
      });
      setCreditCards(updatedCreditCard);
      setSortByCreditCardId("desc");
    }
  };

  const handleSearchButtonClick = async () => {
    window.history.pushState("", "", "/admin/creditcard");
    setIsLoading(true);
    setIsFetchFailed(false);
    let data: any = {
      sort_by: sortBy,
      limit: limit?.value,
    };
    if (isGeneralFilterActive === true) {
      data = {
        ...data,
        isverified: isVerified,
        islocked: isShiftLocked,
        iscompleted: isShiftCompleted,
      };
    }
    if (isAmountFilterActive === true) {
      if (minAmount !== undefined && !isNaN(minAmount)) {
        data = {
          ...data,
          "amount.gteq": minAmount,
        };
      }
      if (maxAmount !== undefined && !isNaN(maxAmount)) {
        data = {
          ...data,
          "amount.lteq": maxAmount,
        };
      }
    }
    if (
      isBatchIdFilterActive === true &&
      batchId !== undefined &&
      batchId !== ""
    ) {
      data = {
        ...data,
        batchid: batchId,
      };
    }
    if (isShiftEntityIdSearchActive === true) {
      if (firstShift !== undefined && firstShift !== null) {
        data = {
          ...data,
          firstshiftsentityid: firstShift.value,
        };
      }
      if (lastShift !== undefined && lastShift !== null) {
        data = {
          ...data,
          lastshiftsentityid: lastShift.value,
        };
      }
    }
    if (
      isBankFilterActive === true &&
      currentCompanyBank !== undefined &&
      currentCompanyBank !== null
    ) {
      data = {
        ...data,
        companybankid: currentCompanyBank.value,
      };
    }
    const queryParams = encodeQueryData(data);
    try {
      const response = await AdminService.getCreditCard(queryParams);

      const updatedCreditCards = response.data.map(
        (creditCard: ICreditCard) => {
          return {
            ...creditCard,
            isVerifiedLoading: false,
            isVerifiedPatchFailed: false,
            isVerifiedSaved: true,
            isDeleteFailed: false,
          };
        }
      );
      console.log(updatedCreditCards);
      setCreditCards(updatedCreditCards);
      setIsLoading(false);
    } catch (err: any) {
      console.dir(err);
      /*
      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.message
      ) {
        setNotificationMessage(err.response.data.message);
      } else {
        setNotificationMessage("Can not reach server.");
      }
      setShowNotificationModal(true);
      */
      dispatch(setGlobalAlert(
        err.response?.data?.message || "Cannot reach server.",
        'error',
        err.response?.data?.details
      ));
      setIsLoading(false);
      setIsFetchFailed(true);
    }
  };

  const handleChangeCompanyBank = (option: SingleValue<IOption>) => {
    setCurrentCompanyBank(option);
  };

  const handleChangeIsBankFilterActive = () => {
    setIsBankFilterActive(!isBankFilterActive);
  };

  const handleChangeIsShiftEntityIdSearchActive = () => {
    if (isShiftEntityIdSearchActive === true) {
      setFirstShift(null);
      setLastShift(null);
    }
    setIsShiftEntityIdSearchActive(!isShiftEntityIdSearchActive);
  };

  const handleChangeFirstShift = (option: SingleValue<IOption>) => {
    console.log(option?.value);
    setFirstShift(option);
    if (option == null) {
      setLastShift(option);
    }
    if (
      lastShift == null ||
      (option != null && option?.value > lastShift.value)
    ) {
      setLastShift(option);
      const updatedSelectOptions = firstShiftSelectOptions?.filter(
        (opt: IOption) => {
          if (option && opt.value >= option?.value) {
            return true;
          }
          return false;
        }
      );
      setLastShiftSelectOptions(updatedSelectOptions);
    }
  };

  const handleChangeLastShift = (option: SingleValue<IOption>) => {
    console.log(option?.value);
    setLastShift(option);
  };

  const handleChangeSortBy = (event: ChangeEvent<HTMLInputElement>) => {
    setSortBy(event.target.value);
  };

  const handleChangeLimit = (option: SingleValue<IOption>) => {
    setLimit(option);
  };

  const handleChangeIsBatchIdFilterActive = () => {
    setIsBatchIdFilterActive(!isBatchIdFilterActive);
  };

  const handleChangeBatchIdInputChanged = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setBatchId(event.target.value);
  };

  const handleChangeIsAmountFilterActive = () => {
    setIsAmountFilterActive(!isAmountFilterActive);
  };

  const handleChangeMinAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const minAmount = Number.parseFloat(event.target.value);
    setMinAmount(minAmount);
  };

  const handleCheckAmountsMin = (event: ChangeEvent<HTMLInputElement>) => {
    const minAmount = Number.parseFloat(event.target.value);
    if (maxAmount && maxAmount < minAmount) {
      setMaxAmount(NaN);
    }
  };

  const handleChangeMaxAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const maxAmount = Number.parseFloat(event.target.value);
    setMaxAmount(maxAmount);
  };

  const handleCheckAmountsMax = (event: ChangeEvent<HTMLInputElement>) => {
    const maxAmount = Number.parseFloat(event.target.value);
    if (minAmount && minAmount > maxAmount) {
      setMinAmount(NaN);
    }
  };

  const handleChangeIsShiftLocked = () => {
    setIsShiftLocked(!isShiftLocked);
  };

  const handleChangeIsShiftCompleted = () => {
    setIsShiftCompleted(!isShiftCompleted);
  };

  const handleChangeIsVerifiedFilter = () => {
    setIsVerified(!isVerified);
  };

  const handleChangeIsGeneralFilterActive = () => {
    setIsGeneralFilterActive(!isGeneralFilterActive);
  };

  const handleChangeIsVerified = async (
    event: any,
    creditCardEntityId: number
  ) => {
    const isCreditCardVerified = event.target.value === "true";
    console.log(isCreditCardVerified);
    const updatedCreditCardsLoading = creditCards?.map(
      (creditCard: ICreditCard) => {
        if (creditCardEntityId === creditCard.shiftCreditCardEntityId) {
          creditCard.isVerifiedLoading = true;
          creditCard.isVerifiedPatchFailed = false;
          creditCard.isVerifiedSaved = false;
          creditCard.verified = isCreditCardVerified;
          return creditCard;
        }
        return creditCard;
      }
    );

    setCreditCards(updatedCreditCardsLoading);
    try {
      const response = await AdminService.patchCreditCard(
        { verified: isCreditCardVerified },
        creditCardEntityId
      );
      const updatedCreditCards = creditCards?.map((creditCard: ICreditCard) => {
        if (creditCardEntityId === creditCard.shiftCreditCardEntityId) {
          creditCard.isVerifiedLoading = false;
          creditCard.isVerifiedPatchFailed = false;
          creditCard.isVerifiedSaved = true;
          return creditCard;
        }
        return creditCard;
      });
      setCreditCards(updatedCreditCards);
    } catch (err: any) {
      console.dir(err);
      /*
      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.message
      ) {
        setNotificationMessage(err.response.data.message);
      } else {
        setNotificationMessage("Can not reach server.");
      }
      setShowNotificationModal(true);
      */
      dispatch(setGlobalAlert(
        err.response?.data?.message || "Cannot reach server.",
        'error',
        err.response?.data?.details
      ));
      const updatedCreditCards = creditCards?.map((creditCard: ICreditCard) => {
        if (creditCardEntityId === creditCard.shiftCreditCardEntityId) {
          creditCard.isVerifiedLoading = false;
          creditCard.isVerifiedPatchFailed = true;
          creditCard.isVerifiedSaved = false;
          creditCard.verified = !isCreditCardVerified;
          return creditCard;
        }
        return creditCard;
      });
      setCreditCards(updatedCreditCards);
    }
  };

  const result = (
    <div className={classNames(styles["credit-card__result__table-wrapper"])}>
      <table className={classNames(styles["credit-card__result__table"])}>
        <thead>
          <tr className={classNames(styles["credit-card__result__table__tr"])}>
            <th
              onClick={handleSortByCreditCardId}
              className={classNames(styles["credit-card__result__table__th"])}
            >
              <span
                className={classNames(
                  styles["credit-card__result__table__span"]
                )}
              >
                <img
                  className={classNames(
                    styles["credit-card__result__table__img"]
                  )}
                  src={
                    sortByCreditCardId === "desc" ? SortDescIcon : SortAscIcon
                  }
                  alt="sort_asc_icon"
                />
                <label className={classNames(styles["credit-card__result__table__label"])}>
                  {languageData?.data?.admin?.creditCard?.table?.creditCardId}
                </label>
              </span>
            </th>

            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.bankName}
            </th>
            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.batchId}
            </th>
            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.amount}
            </th>
            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.verified}
            </th>
            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.shiftId}
            </th>
            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.locked}
            </th>
            <th className={classNames(styles["credit-card__result__table__th"])}>
              {languageData?.data?.admin?.creditCard?.table?.completed}
            </th>
          </tr>
        </thead>
        <tbody>
          {creditCards?.map((creditCard: ICreditCard) => {
            return (
              <tr
                className={classNames(styles["credit-card__result__table__tr"])}
                key={creditCard.shiftCreditCardEntityId}
              >
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.shiftCreditCardEntityId}
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.bankName}
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.batchId}
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.amount}
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  <select
                    disabled={creditCard.isVerifiedLoading}
                    value={`${creditCard.verified}`}
                    onChange={(event) =>
                      handleChangeIsVerified(
                        event,
                        creditCard.shiftCreditCardEntityId
                      )
                    }
                  >
                    <option value="true">✔</option>
                    <option value="false">❌</option>
                  </select>
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.shiftId}
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.locked === true ? languageData?.data?.admin?.creditCard?.table?.yes : languageData?.data?.admin?.creditCard?.table?.no}
                </td>
                <td
                  className={classNames(
                    styles["credit-card__result__table__td"]
                  )}
                >
                  {creditCard.completed === true ? languageData?.data?.admin?.creditCard?.table?.yes : languageData?.data?.admin?.creditCard?.table?.no}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={classNames(styles.container, styles["credit-card"])}>
      <h1>{languageData?.data?.admin?.creditCard?.title}</h1>
      <div className={classNames(styles["credit-card__filter"])}>
        <div className={classNames(styles["credit-card__filter__general"])}>
          <label>{languageData?.data?.admin?.creditCard?.filters?.filterActive}</label>
          <input
            type="checkbox"
            key="isGeneralFilterActiveCheckbox"
            name="isGeneralFilterActiveCheckbox"
            checked={isGeneralFilterActive}
            onChange={handleChangeIsGeneralFilterActive}
          />

          <div className={classNames(styles["credit-card__filter__general__wrapper"])}>
            <div className={classNames(styles["credit-card__filter__general__checkbox"])}>
              <label>{languageData?.data?.admin?.creditCard?.filters?.locked}</label>
              <input
                type="checkbox"
                key="isShiftLockedCheckbox"
                name="isShiftLockedCheckbox"
                checked={isShiftLocked}
                onChange={handleChangeIsShiftLocked}
                disabled={!isGeneralFilterActive}
              />
            </div>
            <div className={classNames(styles["credit-card__filter__general__checkbox"])}>
              <label>{languageData?.data?.admin?.creditCard?.filters?.completed}</label>
              <input
                type="checkbox"
                key="isShiftCompletedCheckbox"
                name="isShiftCompletedCheckbox"
                checked={isShiftCompleted}
                onChange={handleChangeIsShiftCompleted}
                disabled={!isGeneralFilterActive}
              />
            </div>
            <div className={classNames(styles["credit-card__filter__general__checkbox"])}>
              <label>{languageData?.data?.admin?.creditCard?.filters?.verified}</label>
              <input
                type="checkbox"
                key="isVerified"
                name="isVerified"
                checked={isVerified}
                onChange={handleChangeIsVerifiedFilter}
                disabled={!isGeneralFilterActive}
              />
            </div>
          </div>
        </div>
        <hr />
        <div className={classNames(styles["credit-card__filter__amount"])}>
          <div
            className={classNames(
              styles["credit-card__filter__amount__checkbox"]
            )}
          >
            <label>{languageData?.data?.admin?.creditCard?.filters?.amountFilter}</label>
            <input
              type="checkbox"
              key="amountFilterCheckbox"
              name="isAmountFilterActive"
              checked={isAmountFilterActive}
              onChange={handleChangeIsAmountFilterActive}
            />
          </div>
          <div
            className={classNames(
              styles["credit-card__filter__amount__amount"]
            )}
          >
            <div
              className={classNames(
                styles["credit-card__filter__amount__amount__input"]
              )}
            >
              <span>&#8378;</span>
              <input
                type="number"
                placeholder={languageData?.data?.admin?.creditCard?.filters?.minAmount}
                disabled={!isAmountFilterActive}
                onChange={handleChangeMinAmount}
                value={minAmount}
                onBlur={handleCheckAmountsMin}
              />
            </div>

            <div
              className={classNames(
                styles["credit-card__filter__amount__amount__input"]
              )}
            >
              <span>&#8378;</span>
              <input
                type="number"
                placeholder={languageData?.data?.admin?.creditCard?.filters?.maxAmount}
                disabled={!isAmountFilterActive}
                onChange={handleChangeMaxAmount}
                value={maxAmount}
                onBlur={handleCheckAmountsMax}
              />
            </div>
          </div>
        </div>
        <hr />
        <div
          className={classNames(
            styles["credit-card__filter__batch-id"],
            styles["credit-card__filter__block"]
          )}
        >
          <div
            className={classNames(
              styles["credit-card__filter__batch-id__checkbox"]
            )}
          >
            <label>{languageData?.data?.admin?.creditCard?.filters?.batchIdFilter}</label>
            <input
              type="checkbox"
              key="isBatchIdFilterActive"
              name="isBatchIdFilterActive"
              onChange={handleChangeIsBatchIdFilterActive}
              checked={isBatchIdFilterActive}
            />
          </div>
          <div
            className={classNames(
              styles["credit-card__filter__batch-id__input"]
            )}
          >
            <label>{languageData?.data?.admin?.creditCard?.filters?.batchId}</label>
            <input
              type="text"
              name="batchId"
              value={batchId}
              onChange={handleChangeBatchIdInputChanged}
              disabled={!isBatchIdFilterActive}
            />
          </div>
        </div>
        <hr />
        {firstShiftSelectOptions && firstShiftSelectOptions.length > 0 && (
          <div
            className={classNames(
              styles["credit-card__filter__shift-id"],
              styles["credit-card__filter__block"]
            )}
          >
            <div
              className={classNames(
                styles["credit-card__filter__shift-id__checkbox"]
              )}
            >
              <label>{languageData?.data?.admin?.creditCard?.filters?.searchByIdFilter}</label>
              <input
                key="isShiftEntityIdSearchActive"
                type="checkbox"
                value="isShiftEntityIdSearchActive"
                name="isShiftEntityIdSearchActive"
                onChange={handleChangeIsShiftEntityIdSearchActive}
                checked={isShiftEntityIdSearchActive}
              />
            </div>

            <div
              className={
                styles["credit-card__filter__shift-id__dropdown-container"]
              }
            >
              <div
                className={classNames(
                  styles[
                    "credit-card__filter__shift-id__dropdown-container__dropdown"
                  ],
                  styles[
                    "credit-card__filter__shift-id__dropdown-container__dropdown--first-shift"
                  ]
                )}
              >
                <div
                  className={classNames(
                    styles[
                      "credit-card__filter__shift-id__dropdown-container__dropdown__select"
                    ],
                    styles["credit-card__filter__dropbox--long-text"]
                  )}
                >
                  <label>{languageData?.data?.admin?.creditCard?.filters?.firstShift}</label>
                  <Select
                    options={firstShiftSelectOptions}
                    value={firstShift}
                    onChange={handleChangeFirstShift}
                    isSearchable={true}
                    isClearable={true}
                    placeholder={languageData?.data?.admin?.creditCard?.filters?.firstShift}
                    isDisabled={!isShiftEntityIdSearchActive}
                  />
                </div>
              </div>

              <div
                className={classNames(
                  styles[
                    "credit-card__filter__shift-id__dropdown-container__dropdown"
                  ],
                  styles[
                    "credit-card__filter__shift-id__dropdown-container__dropdown--last-shift"
                  ]
                )}
              >
                <div
                  className={classNames(
                    styles[
                      "credit-card__filter__shift-id__dropdown-container__dropdown__select"
                    ],
                    styles["credit-card__filter__dropbox--long-text"]
                  )}
                >
                  <label>{languageData?.data?.admin?.creditCard?.filters?.lastShift}</label>
                  {lastShiftSelectOptions !== undefined &&
                    lastShiftSelectOptions !== null && (
                      <Select
                        options={lastShiftSelectOptions}
                        value={lastShift}
                        onChange={handleChangeLastShift}
                        isSearchable={true}
                        isClearable={true}
                        placeholder={languageData?.data?.admin?.creditCard?.filters?.lastShift}
                        isDisabled={!isShiftEntityIdSearchActive}
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
        <hr />
        <div className={styles["credit-card__filter__company-bank-id"]}>
          <div
            className={styles["credit-card__filter__company-bank-id__checkbox"]}
          >
            <label>{languageData?.data?.admin?.creditCard?.filters?.bankFilterActive}</label>
            <input
              type="checkbox"
              name="isBankFilterActive"
              value="isBankFilterActive"
              onChange={handleChangeIsBankFilterActive}
              checked={isBankFilterActive}
            />
          </div>
          <div
            className={classNames(
              styles["credit-card__filter__company-bank-id__dropdown"],
              styles["credit-card__filter__dropbox--long-text"]
            )}
          >
            <label>{languageData?.data?.admin?.creditCard?.filters?.bankFilter}</label>
            <Select
              options={companyBanks}
              value={currentCompanyBank}
              onChange={handleChangeCompanyBank}
              isSearchable={false}
              isDisabled={!isBankFilterActive}
              isClearable={true}
              placeholder={languageData?.data?.admin?.creditCard?.filters?.selectBank}
            />
          </div>
        </div>
        <hr />
        <div className={styles["credit-card__filter__search-options"]}>
          <div className={styles["credit-card__filter__search-options__radio"]}>
            <label>{languageData?.data?.admin?.creditCard?.filters?.sortBy}</label>
            <input
              type="radio"
              value="asc"
              onChange={handleChangeSortBy}
              checked={sortBy === "asc"}
            />
          </div>
          <div className={styles["credit-card__filter__search-options__radio"]}>
            <label>{languageData?.data?.admin?.creditCard?.filters?.sortByDesc}</label>
            <input
              type="radio"
              value="desc"
              onChange={handleChangeSortBy}
              checked={sortBy === "desc"}
            />
          </div>
          <div
            className={styles["credit-card__filter__search-options__dropdown"]}
          >
            <label>{languageData?.data?.admin?.creditCard?.filters?.listAmount}</label>
            <Select
              options={limitOptions}
              value={limit}
              onChange={handleChangeLimit}
              isSearchable={false}
            />
          </div>
        </div>
        <hr />
        <Button onClick={handleSearchButtonClick}>
          {languageData?.data?.admin?.creditCard?.filters?.search}
        </Button>
        <hr />
      </div>
      <div className={classNames(styles["credit-card__result"])}>
        {isLoading === true && isFetchFailed === false && <Spinner />}
        {isLoading === false && isFetchFailed === true && <h2>Fetch Failed</h2>}
        {creditCards !== undefined && creditCards.length === 0 && (
          <h2>{languageData?.data?.admin?.creditCard?.noResults}</h2>
        )}
        {creditCards !== undefined && creditCards.length > 0 && result}
      </div>
    </div>
  );
}
